import { useTranslation } from "react-i18next";
import React, { useImperativeHandle, forwardRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Truck, Store } from "lucide-react";

const checkoutFormSchema = z.object({
  title: z.string().max(255),
  fulfillmentType: z.enum(["delivery", "pickup"], {
    required_error: "Please select a fulfillment type",
  }),
  notes: z.string().max(1000).optional(),
});

export type CheckoutFormData = z.infer<typeof checkoutFormSchema>;

/** Imperative handle so parent can trigger submit without embedding buttons. */
export interface CheckoutFormRef {
  submit: () => void;
  saveDraft: () => void;
}

interface CheckoutFormProps {
  initialData?: Partial<CheckoutFormData>;
  suggestedTitle?: string;
  onSubmit: (data: CheckoutFormData) => void;
  onSaveDraft?: (data: CheckoutFormData) => void;
  disabled?: boolean;
}

export const CheckoutForm = forwardRef<CheckoutFormRef, CheckoutFormProps>(
  function CheckoutForm(
    { initialData, suggestedTitle, onSubmit, onSaveDraft, disabled = false },
    ref,
  ) {
    const { t } = useTranslation();
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = useForm<CheckoutFormData>({
      resolver: zodResolver(checkoutFormSchema),
      defaultValues: {
        title: initialData?.title ?? "",
        fulfillmentType: initialData?.fulfillmentType ?? "pickup",
        notes: initialData?.notes ?? "",
      },
    });

    const fulfillmentType = watch("fulfillmentType");

    const handleFormSubmit = (data: CheckoutFormData) => {
      onSubmit({
        ...data,
        title: data.title.trim() || suggestedTitle || "",
      });
    };

    const handleDraftSubmit = (data: CheckoutFormData) => {
      onSaveDraft?.({
        ...data,
        title: data.title.trim() || suggestedTitle || "",
      });
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit(handleFormSubmit)(),
      saveDraft: () => handleSubmit(handleDraftSubmit)(),
    }));

    return (
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-4"
        id="checkout-form"
      >
        {/* PO Number — compact single row */}
        <div className="space-y-1.5">
          <Label htmlFor="title" className="text-sm">
            {t("components.orders.checkoutForm.poNumber")}
          </Label>
          <Input
            id="title"
            placeholder={suggestedTitle}
            disabled={disabled}
            className="h-9"
            {...register("title")}
          />
          {errors.title && (
            <p className="text-xs text-destructive">{errors.title.message}</p>
          )}
        </div>

        {/* Fulfillment type — compact toggle */}
        <div className="space-y-1.5">
          <Label className="text-sm">{t("components.orders.checkoutForm.fulfillment")}</Label>
          <div className="flex gap-2">
            <button
              type="button"
              aria-pressed={fulfillmentType === "pickup"}
              disabled={disabled}
              onClick={() =>
                setValue("fulfillmentType", "pickup", { shouldValidate: true })
              }
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 text-sm transition-colors ${
                fulfillmentType === "pickup"
                  ? "border-primary bg-primary/5 font-medium"
                  : "border-border hover:border-muted-foreground"
              }`}
            >
              <Store className="h-4 w-4" aria-hidden="true" />
              {t("components.orders.checkoutForm.pickup")}
            </button>
            <button
              type="button"
              aria-pressed={fulfillmentType === "delivery"}
              disabled={disabled}
              onClick={() =>
                setValue("fulfillmentType", "delivery", {
                  shouldValidate: true,
                })
              }
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 text-sm transition-colors ${
                fulfillmentType === "delivery"
                  ? "border-primary bg-primary/5 font-medium"
                  : "border-border hover:border-muted-foreground"
              }`}
            >
              <Truck className="h-4 w-4" aria-hidden="true" />
              {t("components.orders.checkoutForm.delivery")}
            </button>
          </div>
          {errors.fulfillmentType && (
            <p className="text-xs text-destructive">
              {errors.fulfillmentType.message}
            </p>
          )}
        </div>

        {/* Notes — smaller textarea */}
        <div className="space-y-1.5">
          <Label htmlFor="notes" className="text-sm">
            {t("components.orders.checkoutForm.notes")}{" "}
            <span className="text-muted-foreground font-normal">
              {t("components.orders.checkoutForm.optional")}
            </span>
          </Label>
          <Textarea
            id="notes"
            rows={2}
            disabled={disabled}
            placeholder={t("components.orders.checkoutForm.deliveryInstructionsSpecialRequests")}
            {...register("notes")}
          />
          {errors.notes && (
            <p className="text-xs text-destructive">{errors.notes.message}</p>
          )}
        </div>
      </form>
    );
  },
);
